import LANGUAGES from '../../_json/_languages.json';

class LocaleService 
{
	constructor() 
	{
		this.localeCache = {};
		this.loadingPromises = {};
		this.defaultLang = LANGUAGES.default;
	}

	/**
	 * Get available languages (only enabled ones)
	 */
	getAvailableLanguages() 
	{
		return LANGUAGES.languages.filter(lang => lang.enabled !== false);
	}

	/**
	 * Get language configuration
	 */
	getLanguageConfig(langCode) 
	{
		return LANGUAGES.languages.find(lang => lang.code === langCode);
	}

	/**
	 * Check if voiceover is enabled for a language
	 */
	isVoiceoverEnabled(langCode) 
	{
		const config = this.getLanguageConfig(langCode);
		return config ? (config.voiceover_enabled !== false) : true;
	}

	/**
	 * Generic locale getter (synchronous) - for cached data only
	 * Can access any top-level key in locale JSON (terms, messages, etc.)
	 */
	getLocale(langCode, key, parentKey = 'terms', fallback = '') 
	{
		const localeData = this.localeCache[langCode];
		if (localeData?.[parentKey]?.[key]) 
		{
			return localeData[parentKey][key];
		}
		
		// Fallback to default language
		const defaultData = this.localeCache[this.defaultLang];
		if (defaultData?.[parentKey]?.[key]) 
		{
			return defaultData[parentKey][key];
		}
		
		return fallback || key;
	}

	/**
	 * Get text from 'terms' section (synchronous) - for cached data only
	 */
	getTerm(langCode, key, fallback = '') 
	{
		return this.getLocale(langCode, key, 'terms', fallback);
	}

	/**
	 * Get text from 'messages' section (synchronous) - for cached data only
	 */
	getMessage(langCode, key, fallback = '') 
	{
		return this.getLocale(langCode, key, 'messages', fallback);
	}

	/**
	 * @deprecated Use getTerm() instead
	 */
	getText(langCode, key, fallback = '') 
	{
		return this.getTerm(langCode, key, fallback);
	}

	/**
	 * Get building/exterior/restaurant localized data (synchronous) - for cached data only
	 */
	getItemData(langCode, type, id) 
	{
		const localeData = this.localeCache[langCode];
		
		if (!localeData) return null;
		
		// Handle map-labels which is an object, not an array
		if (type === 'map-labels') 
		{
			return localeData['map-labels']?.[id] || null;
		}
		
		const items = localeData[type]; // buildings, exteriors, restaurants, stampcard, etc.
		const result = items?.find(item => item.id === id) || null;
		
		return result;
	}

	/**
	 * Get all items of a specific type (synchronous) - for cached data only
	 */
	getAllItems(langCode, type) 
	{
		const localeData = this.localeCache[langCode];
		if (!localeData) return [];
		
		return localeData[type] || [];
	}

	/**
	 * Load locale data for a specific language
	 */
	async loadLocale(langCode) 
	{
		// Return cached data if available
		if (this.localeCache[langCode]) 
		{
			return this.localeCache[langCode];
		}

		// Return existing loading promise if in progress
		if (this.loadingPromises[langCode]) 
		{
			return this.loadingPromises[langCode];
		}

		// Find language configuration
		const langConfig = this.getLanguageConfig(langCode);
		if (!langConfig) 
		{
			console.warn(`Language configuration not found: ${langCode}`);
			return null;
		}

		// Create loading promise
		this.loadingPromises[langCode] = this._fetchLocaleData(langConfig);

		try 
		{
			const data = await this.loadingPromises[langCode];
			this.localeCache[langCode] = data;
			delete this.loadingPromises[langCode];
			return data;
		} 
		catch (error) 
		{
			console.error(`Failed to load locale: ${langCode}`, error);
			delete this.loadingPromises[langCode];
			return null;
		}
	}

	/**
	 * Fetch locale data from server
	 */
	async _fetchLocaleData(langConfig) 
	{
		const response = await fetch(`${process.env.PUBLIC_URL}/json/locale/${langConfig.file}`);
		if (!response.ok) 
		{
			throw new Error(`HTTP ${response.status}: ${response.statusText}`);
		}
		return await response.json();
	}

	/**
	 * Get localized text with fallback support (async)
	 */
	async getLocalizedText(langCode, key, fallback = '') 
	{
		// Load locale data if not cached
		let localeData = this.localeCache[langCode];
		if (!localeData) 
		{
			localeData = await this.loadLocale(langCode);
		}

		// Try to get text from requested language
		if (localeData?.terms?.[key]) 
		{
			return localeData.terms[key];
		}

		// Fallback to default language if different
		if (langCode !== this.defaultLang) 
		{
			let defaultLocaleData = this.localeCache[this.defaultLang];
			if (!defaultLocaleData) 
			{
				defaultLocaleData = await this.loadLocale(this.defaultLang);
			}

			if (defaultLocaleData?.terms?.[key]) 
			{
				return defaultLocaleData.terms[key];
			}
		}

		// Return fallback if provided
		return fallback || key;
	}

	/**
	 * Get localized data for buildings, exteriors, etc. (async)
	 */
	async getLocalizedData(langCode, dataType, id) 
	{
		const localeData = await this.loadLocale(langCode);
		
		if (!localeData) return null;

		let result = null;
		
		// Handle different data types
		switch (dataType) 
		{
			case 'building':
			case 'buildings':
				result = localeData.buildings?.find(item => item.id === id);
				break;
			case 'exterior':
			case 'exteriors':
				result = localeData.exteriors?.find(item => item.id === id);
				break;
			case 'restaurant':
			case 'restaurants':
				result = localeData.restaurants?.find(item => item.id === id);
				break;
			case 'stampcard':
				result = localeData.stampcard || [];
				break;
			case 'message':
			case 'messages':
				result = localeData.messages?.[id];
				break;
			default:
				console.warn(`Unknown data type: ${dataType}`);
				return null;
		}
		
		return result;
	}

	/**
	 * Preload all enabled locales for instant language switching
	 */
	async preloadEssentialLocales(currentLang) 
	{
		const promises = [];
		
		// Load all enabled languages for instant switching
		const enabledLanguages = this.getAvailableLanguages();
		enabledLanguages.forEach(langConfig => {
			promises.push(this.loadLocale(langConfig.code));
		});

		await Promise.all(promises);
	}

	/**
	 * Clear cache (useful for development)
	 */
	clearCache() 
	{
		this.localeCache = {};
		this.loadingPromises = {};
	}
}

// Export singleton instance
export default new LocaleService(); 